import { IkeUnitAngle } from './units';

/** 2 * Math.Pi */
export const TWO_PI = Math.PI * 2;

/** Utility to help with working with angles */
export class IkeAngle {
	static RADIAN: typeof IkeUnitAngle.RADIAN = IkeUnitAngle.RADIAN;
	static DEGREE: typeof IkeUnitAngle.DEGREE = IkeUnitAngle.DEGREE;

	/**
	 * Case sensitive search for unit type
	 * @see IkeAngle.toUnitAngle
	 */
	static isUnitAngle(unit: string): unit is IkeUnitAngle {
		return unit === IkeAngle.RADIAN || unit === IkeAngle.DEGREE;
	}

	/** Case insensitive angle unit type search */
	static toUnitAngle(unit: string): IkeUnitAngle | null {
		let searchUnit = unit.toLowerCase();
		if (IkeAngle.isUnitAngle(searchUnit)) {
			return searchUnit;
		}
		return null;
	}
	/**
	 * Convert an angle to be between 0 and 2 * pi
	 * @param value Angle (Radians)
	 */
	static normalize(value: number) {
		return (value + TWO_PI) % TWO_PI;
	}

	/**
	 * Rotate an angle by pi
	 * @param value Angle (Radians)
	 */
	static flip(value: number) {
		return (value + Math.PI) % TWO_PI;
	}

	/**
	 * Convert an angle from degrees to radians
	 * @param degrees Angle (Degrees)
	 */
	static toRadians(degrees: number) {
		return degrees * (Math.PI / 180);
	}

	/**
	 * Convert an angle from radian to degrees
	 * @param radians Angle (Radians)
	 */
	static toDegrees(radians: number) {
		return radians * (180 / Math.PI);
	}

	/**
	 * Normalize a value into the degrees range `[0, 360)`.
	 *
	 * The result is rounded to 10 decimal places so IEEE-754 artifacts from the modulo
	 * (e.g. `435.547 % 360 === 75.54700000000003`) don't leak into stored values or UI
	 * previews. If rounding pushes the result up to exactly `360` it wraps back to `0`.
	 *
	 * @param deg The un-normalized Degrees value
	 */
	static normalizeDegrees(deg: number) {
		const wrapped = ((deg % 360) + 360) % 360;
		const rounded = Math.round(wrapped * 1e10) / 1e10;
		return rounded >= 360 ? 0 : rounded;
	}
}
